import { memo, useMemo } from 'react';
import { Box, Group, Stack, Text, Tooltip } from '@mantine/core';
import { AlertTriangle, Bell, Shield, Zap } from 'lucide-react';
import { Badge, Card } from '../ui';
import { alpha, colors, inks, type ColorToken } from '../../theme/tokens';
import { byRecencyThenSeverity, clockTime, relativeAge } from '../../lib/alertTime';

export type AlertSeverity = 'info' | 'warning' | 'critical';

export interface AlertItem {
  id: string;
  ts: string;
  source: 'wazuh' | 'cloudwatch' | 'caldera' | 'forge';
  severity: AlertSeverity;
  title: string;
  message: string;
  rangeId?: string;
}

interface AlertFeedProps {
  items: ReadonlyArray<AlertItem>;
}

const ICON_BY_SOURCE = { wazuh: Shield, cloudwatch: Bell, caldera: Zap, forge: AlertTriangle };

/**
 * Severity drives COLOR, source drives the glyph.
 *
 * This was the other way round: the icon was tinted per source and severity
 * lived only in a small text badge, so a `critical` row and an `info` row were
 * the same colour and an analyst could not rank the feed by eye — the one thing
 * a triage list has to do.
 */
const SEVERITY_TOKEN: Record<AlertSeverity, ColorToken> = {
  critical: 'hudRed',
  warning: 'hudAmber',
  info: 'hudCyan',
};

const SEVERITY_INK: Record<AlertSeverity, string> = {
  critical: inks.hudRed,
  warning: inks.hudAmber,
  info: inks.hudCyan,
};

const VARIANT_BY_SEVERITY: Record<AlertSeverity, 'info' | 'warn' | 'danger'> = {
  info: 'info',
  warning: 'warn',
  critical: 'danger',
};

function AlertFeedInner({ items }: AlertFeedProps) {
  const now = Date.now();

  // Newest first. The feed used to render in whatever order the API returned,
  // which put 11:30 above 11:34 on screen — in a triage list that reads as a
  // timeline, and a timeline that is not in order is worse than no timeline.
  const ordered = useMemo(() => byRecencyThenSeverity(items), [items]);

  if (ordered.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No alerts in the last 24h.
      </Text>
    );
  }

  return (
    <Stack gap={6}>
      {ordered.map((item) => {
        const token = SEVERITY_TOKEN[item.severity];
        const Icon = ICON_BY_SOURCE[item.source];
        return (
          <Card
            key={item.id}
            padding="sm"
            style={{
              // A severity rail down the leading edge: scannable at a glance,
              // and it survives both themes because it is an accent token.
              borderInlineStartWidth: 3,
              borderInlineStartColor: colors[token],
            }}
          >
            <Group justify="space-between" wrap="nowrap" align="start">
              <Group gap="sm" wrap="nowrap" align="start">
                <Box
                  aria-hidden
                  style={{
                    display: 'grid',
                    placeItems: 'center',
                    width: 26,
                    height: 26,
                    flex: '0 0 auto',
                    borderRadius: 7,
                    backgroundColor: alpha(token, 0.12),
                  }}
                >
                  <Icon size={14} color={SEVERITY_INK[item.severity]} />
                </Box>
                <Stack gap={2}>
                  <Group gap="xs">
                    <Text size="sm" fw={600} c="rgb(var(--c-txt-bright))">
                      {item.title}
                    </Text>
                    <Badge variant={VARIANT_BY_SEVERITY[item.severity]} size="xs">
                      {item.severity}
                    </Badge>
                  </Group>
                  <Text size="xs" c="dimmed">
                    {item.message}
                  </Text>
                </Stack>
              </Group>
              <Stack gap={2} align="end" style={{ flex: '0 0 auto' }}>
                {/* The full ISO timestamp is still one hover away; it just no
                    longer costs eight characters of millisecond noise in the
                    row an analyst reads a hundred times. */}
                <Tooltip label={item.ts} withArrow openDelay={250}>
                  <Text size="xs" c="dimmed" ff="JetBrains Mono" style={{ cursor: 'default' }}>
                    {clockTime(item.ts)}
                  </Text>
                </Tooltip>
                <Group gap={6} wrap="nowrap">
                  <Text size="xs" c="dimmed">
                    {relativeAge(item.ts, now)}
                  </Text>
                  {item.rangeId ? (
                    <Badge variant="neutral" size="xs">
                      {item.rangeId}
                    </Badge>
                  ) : null}
                </Group>
              </Stack>
            </Group>
          </Card>
        );
      })}
    </Stack>
  );
}

export const AlertFeed = memo(AlertFeedInner);
